import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Headphones, ListChecks, ListPlus, ListOrdered } from 'lucide-react'
import { AppLink } from '@/components/ui/app-link'
import { cn } from '@/lib/utils'
import { sdk } from '@/lib/sdk'
import { usePlayer } from './PlayerProvider'
import { ProgressBar, chapterAt, formatClock } from './ProgressBar'
import { Equalizer, NextButton, PlayPauseButton, PlayerIconButton, PreviousButton, QueueButton, SkipButton, SleepMenu, SpeedMenu } from './PlayerControls'
import { paletteStyle, useCoverPalette } from './coverColor'
import { PlayerToneContext } from './tone'
import type { PlayerChapter, PlayerEpisode } from './types'

/** The episode's cover: its own, the video's frame, else the show's (signed on demand). */
export function useEpisodeCover(episode: PlayerEpisode | null): string | null {
  const direct = episode?.media?.cover?.url || episode?.media?.video?.thumbnailUrl || null
  const showCoverId = direct ? null : episode?.show?.coverMediaId || null
  const [showCover, setShowCover] = React.useState<string | null>(null)
  React.useEffect(() => {
    setShowCover(null)
    if (!showCoverId) return
    let canceled = false
    sdk.media.getUrl(showCoverId)
      .then((signed) => { if (!canceled) setShowCover(signed?.url || signed?.thumbnailUrl || null) })
      .catch(() => undefined)
    return () => { canceled = true }
  }, [showCoverId])
  return direct || showCover
}

export function EpisodeCover({ url, title, className }: { url: string | null; title: string; className?: string }) {
  return url ? (
    <img src={url} alt="" title={title} className={cn('aspect-square shrink-0 rounded-lg object-cover', className)} />
  ) : (
    <div aria-hidden className={cn('flex aspect-square shrink-0 items-center justify-center rounded-lg bg-[rgb(var(--pl-accent)/0.12)] text-[rgb(var(--pl-accent-text))]', className)}>
      <Headphones className="h-1/3 w-1/3" />
    </div>
  )
}

interface PodcastPlayerProps {
  /** The episode detail (`GET /api/media-episodes/{id}`), with `media` signed. */
  episode: PlayerEpisode
  /** The show's page: the show name links there. */
  showHref?: string | null
  className?: string
}

/** The chapter list: the playing one highlighted, with the equalizer while it plays. */
export function ChapterList({ chapters, current, playing, onSeek, className }: {
  chapters: PlayerChapter[]
  current: PlayerChapter | null
  playing: boolean
  onSeek: (seconds: number) => void
  className?: string
}) {
  const { t } = useTranslation('player')
  return (
    <ol className={cn('space-y-0.5', className)} aria-label={t('chapters.title', { count: chapters.length })}>
      {chapters.map((chapter, index) => {
        const active = current === chapter
        return (
          <li key={`${chapter.startSeconds}-${chapter.title}`}>
            <button
              type="button"
              onClick={() => onSeek(chapter.startSeconds)}
              aria-current={active ? 'true' : undefined}
              className={cn(
                'group flex min-h-11 w-full items-center gap-3 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--pl-accent))]',
                active ? 'bg-[rgb(var(--pl-accent)/0.1)] font-semibold text-[rgb(var(--pl-accent-text))]' : 'text-foreground'
              )}
            >
              <span className="flex w-6 shrink-0 justify-center text-xs tabular-nums text-muted-foreground">
                {active ? <Equalizer paused={!playing} className="text-[rgb(var(--pl-accent-text))]" /> : index + 1}
              </span>
              <span className="min-w-0 flex-1">{chapter.title}</span>
              <span className={cn('shrink-0 text-xs tabular-nums', active ? 'text-[rgb(var(--pl-accent-text))]' : 'text-muted-foreground')}>{formatClock(chapter.startSeconds)}</span>
            </button>
          </li>
        )
      })}
    </ol>
  )
}

/**
 * F-178 criterion 6: the Podcast player of the episode page, Spotify's "Now
 * Playing": the hero in the cover's color (large cover, show, title, chapter),
 * the bar with the chapters, −15 s / play / +30 s around previous and next,
 * then speed, sleep timer, queue and chapters as quiet icons. It drives the
 * one shared player: leaving the page hands the sound to the mini-player
 * without a gap.
 */
export function PodcastPlayer({ episode, showHref, className }: PodcastPlayerProps) {
  const { t } = useTranslation('player')
  const player = usePlayer()
  const { registerFullPlayer, playEpisode, seek, enqueue, removeFromQueue } = player
  const isCurrent = player.current?.id === episode.id
  const rootRef = React.useRef<HTMLDivElement | null>(null)
  const cover = useEpisodeCover(isCurrent && player.coverUrl ? null : episode)
  const coverUrl = isCurrent && player.coverUrl ? player.coverUrl : cover
  const palette = useCoverPalette(coverUrl)
  const [chaptersOpen, setChaptersOpen] = React.useState(false)

  /* While this player is on screen the mini-player steps aside (same episode only). */
  React.useEffect(() => {
    const el = rootRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return registerFullPlayer(episode.id)
    let release: (() => void) | null = null
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !release) release = registerFullPlayer(episode.id)
      if (!entry.isIntersecting && release) { release(); release = null }
    }, { threshold: 0.25 })
    observer.observe(el)
    return () => { observer.disconnect(); release?.() }
  }, [episode.id, registerFullPlayer])

  const resumeAt = episode.progress?.resumeAt ?? 0
  const position = isCurrent ? player.position : resumeAt
  const duration = (isCurrent && player.duration) || episode.durationSeconds || episode.media?.audio?.durationSeconds || episode.media?.video?.durationSeconds || 0
  const chapters = episode.chapters || []
  const currentChapter = chapterAt(chapters, position)
  const playable = Boolean(episode.media?.audio?.url || episode.media?.video?.url)
  const inQueue = player.queue.some((item) => item.episodeId === episode.id)
  const playing = isCurrent && player.status === 'playing'

  const start = (at?: number) => { void playEpisode(episode, at === undefined ? {} : { startAt: at }) }
  const seekTo = (seconds: number) => (isCurrent ? seek(seconds) : start(seconds))

  return (
    <section ref={rootRef} aria-label={t('playerLabel')} style={paletteStyle(palette)} className={cn('overflow-hidden rounded-xl border border-border bg-surface', className)}>
      {/* One continuous card, Spotify style: the cover's color runs behind the controls and fades into the surface at the bottom edge. */}
      <PlayerToneContext.Provider value="onColor">
        <div
          className="text-white"
          style={{ background: 'linear-gradient(180deg, rgb(var(--pl-hero)) 0%, rgb(var(--pl-hero-deep)) calc(100% - 3rem), rgb(var(--rgb-surface)) 100%)' }}
        >
        <div className="px-4 pb-4 pt-6 sm:px-8 sm:pt-8">
          <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-end sm:gap-7">
            <EpisodeCover url={coverUrl} title={episode.title} className="w-52 rounded-lg bg-[rgb(var(--pl-on-hero)/0.15)] text-white shadow-[0_8px_40px_rgba(0,0,0,0.45)] sm:w-48 lg:w-56" />
            <div className="min-w-0 flex-1 text-center sm:pb-1 sm:text-left">
              {episode.show?.title && (
                showHref
                  ? <AppLink to={showHref} className="rounded-sm text-sm font-semibold text-white/90 hover:text-white hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--pl-on-hero))]">{episode.show.title}</AppLink>
                  : <p className="text-sm font-semibold text-white/90">{episode.show.title}</p>
              )}
              <h2 className="mt-1 break-words text-2xl font-extrabold leading-tight tracking-tight sm:text-3xl lg:text-4xl">{episode.title}</h2>
              {currentChapter && (
                <p className="mt-2 flex items-center justify-center gap-2 text-sm font-medium text-white/90 sm:justify-start">
                  {playing && <Equalizer />}
                  <span className="sr-only">{t('chapters.current')}: </span>
                  <span className="min-w-0 truncate">{currentChapter.title}</span>
                </p>
              )}
            </div>
          </div>
        </div>

      <div className="px-4 pb-12 pt-2 sm:px-8">
        <ProgressBar position={position} duration={duration} chapters={chapters} onSeek={seekTo} />
        <div className="mt-0.5 flex justify-between text-xs tabular-nums text-white/90">
          <span>{formatClock(position)}</span>
          <span>{duration ? `−${formatClock(Math.max(0, duration - position))}` : '--:--'}</span>
        </div>

        <div className="mt-2 flex items-center justify-center gap-1 sm:gap-3">
          {isCurrent ? <PreviousButton /> : <span className="w-11" aria-hidden />}
          <SkipButton direction="back" disabled={!isCurrent} />
          {isCurrent ? <PlayPauseButton size="xl" /> : <PlayPauseButton size="xl" onPlay={() => start()} disabled={!playable} />}
          <SkipButton direction="forward" disabled={!isCurrent} />
          {isCurrent ? <NextButton /> : <span className="w-11" aria-hidden />}
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-center gap-1 sm:justify-between">
          <div className="flex items-center gap-1">
            <SpeedMenu />
            {isCurrent && <SleepMenu />}
            {!isCurrent && resumeAt > 0 && (
              <span className="px-2 text-xs text-white/90">{t('resumeAt', { time: formatClock(resumeAt) })}</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {player.canSync && !isCurrent && (
              <PlayerIconButton
                label={inQueue ? t('queue.inQueue') : t('queue.add')}
                active={inQueue}
                aria-pressed={inQueue}
                onClick={() => { void (inQueue ? removeFromQueue(episode.id) : enqueue(episode)) }}
              >
                {inQueue ? <ListChecks className="h-5 w-5" /> : <ListPlus className="h-5 w-5" />}
              </PlayerIconButton>
            )}
            {chapters.length > 0 && (
              <PlayerIconButton label={t('chapters.title', { count: chapters.length })} active={chaptersOpen} aria-expanded={chaptersOpen} onClick={() => setChaptersOpen((open) => !open)}>
                <ListOrdered className="h-5 w-5" />
              </PlayerIconButton>
            )}
            <QueueButton side="bottom" />
          </div>
        </div>
      </div>
        </div>
      </PlayerToneContext.Provider>

      {chapters.length > 0 && chaptersOpen && (
        <div className="border-t border-border px-2 pb-3 pt-3 sm:px-6">
          <p className="px-2 pb-1 text-sm font-bold text-foreground">{t('chapters.title', { count: chapters.length })}</p>
          <ChapterList chapters={chapters} current={currentChapter} playing={playing} onSeek={seekTo} />
        </div>
      )}
    </section>
  )
}
